import { useState, useCallback } from 'react';

interface ConfirmDialogOptions {
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'default' | 'destructive';
}

interface ConfirmDialogState extends ConfirmDialogOptions {
    isOpen: boolean;
    resolve: ((value: boolean) => void) | null;
}

export const useConfirmDialog = () => {
    const [state, setState] = useState<ConfirmDialogState>({
        isOpen: false,
        title: '',
        resolve: null,
    });

    const confirm = useCallback((options: ConfirmDialogOptions): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            setState({
                ...options,
                isOpen: true,
                resolve,
            });
        });
    }, []);

    const handleClose = useCallback(() => {
        setState((prev) => {
            if (prev.resolve) {
                prev.resolve(false);
            }
            return {
                ...prev,
                isOpen: false,
                resolve: null,
            };
        });
    }, []);

    const handleConfirm = useCallback(() => {
        setState((prev) => {
            if (prev.resolve) {
                prev.resolve(true);
            }
            return {
                ...prev,
                isOpen: false,
                resolve: null,
            };
        });
    }, []);

    return {
        ...state,
        confirm,
        onClose: handleClose,
        onConfirm: handleConfirm,
    };
};
